import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, toDisplayString as _toDisplayString, createElementVNode as _createElementVNode, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, createTextVNode as _createTextVNode, withCtx as _withCtx, createVNode as _createVNode } from "vue"

const _hoisted_1 = { id: "modal-container-element" }
const _hoisted_2 = { class: "description" }
const _hoisted_3 = ["actions"]
const _hoisted_4 = { key: 1 }
const _hoisted_5 = ["rows"]

import { useStore } from 'vuex';
import { useRouter } from 'vue-router';
import { computed, ref, onMounted, watchEffect, onUnmounted } from 'vue';

import { startPolling, stopPolling } from '../utils/polling';
import EpinioCatalogServiceModel from '../models/catalogservices';
import { EPINIO_TYPES } from '../types';
import { makeStateTag, makeRouterLink, makeRouterLinksOrEmpty, overrideTableRows } from '../utils/table-formatters';
import ServiceDeleteModal from '../components/service/ServiceDeleteModal.vue';
import ServiceInstanceModal from '../components/service/ServiceInstanceModal.vue';
import EpinioServiceModel from 'models/services';
import { makeActionMenu } from '../utils/table-formatters';
import Masthead from '@shell/components/ResourceList/Masthead';
import CatalogServiceModal from '../components/service/CatalogServiceModal.vue';
import CatalogServiceDeleteModal from '../components/service/CatalogServiceDeleteModal.vue';


export default /*@__PURE__*/_defineComponent({
  __name: 'catalogservices',
  props: {
    value: {}
  },
  setup(__props: any) {

const store = useStore();
const router = useRouter();
const t = store.getters['i18n/t'];

const props = __props;

const serviceDeleteModal = ref<InstanceType<typeof ServiceDeleteModal> | null>(null);
const serviceModal = ref<InstanceType<typeof ServiceInstanceModal> | null>(null);
const catalogServiceModal = ref<InstanceType<typeof CatalogServiceModal> | null>(null);
const catalogServiceDeleteModal = ref<InstanceType<typeof CatalogServiceDeleteModal> | null>(null);
const displayRows = ref<any[]>([]);

const canEditService = computed(() => {
  const can = store.getters['epinio/can'];

  return can && (can('service_write') || can('service'));
});
const canDeleteService = canEditService;
const canCreateService = canEditService;

const canEditCatalogService = computed(() => {
  const can = store.getters['epinio/can'];

  return can && (can('catalog_service_write') || can('catalog_service'));
});
const canDeleteCatalogService = canEditCatalogService;

const availableActions = computed(() => {
    const rowActions = (row: EpinioCatalogServiceModel) => {
    const out: any[] = [];

    if (canCreateService.value) {
      out.push({
        label: t('epinio.services.create.label'),
        enabled: true,
        action: () => serviceModal.value?.openCreate(row.id)
      });
    }
    if (canEditCatalogService.value) {
      out.push({
        label: t('generic.edit'),
        enabled: true,
        action: () => catalogServiceModal.value?.openEdit(row),

      });
    }
    if (canDeleteCatalogService.value) {
      out.push({
        enabled: true,
        label: t('generic.delete'),
        action: () => catalogServiceDeleteModal.value?.openDelete(row),
      });
    }

    return out;
  };

  return rowActions(props.value);
});

watchEffect(() => {
  const all = store.getters['epinio/all'](EPINIO_TYPES.SERVICE_INSTANCE) as any[];

  // Filter empty rows that are added during delete and only show services related to this catalog service
  const filtered = all.filter((row: any) => row.id && row.catalog_service === props.value.id);

  // Build the row action menu with RBAC gating. Inject the modal-driven
  // Edit/Delete entries only when the user has service write permissions.
  const rowActions = (row: EpinioServiceModel) => {
    const out: any[] = [];

    if (canDeleteService.value) {
      out.push({
        action: 'removeService',
        altAction: 'remove',
        bulkAction: 'removeService',
        bulkable: true,
        enabled: row.canDelete,
        icon: 'icon icon-trash',
        label: 'Delete',
        weight: -10
      });
    }
    if (canEditService.value) {
      out.push({
        action: 'editServiceModal',
        label: 'Edit',
        enabled: true
      });
    }

    return out;
  };

  const overrideProps = [
      {
        prop: 'availableActions',
        value: rowActions,
        conditionFn: () => true,
      },
      {
        prop: 'removeService',
        value: (row: EpinioServiceModel) => () => {
          serviceDeleteModal.value?.openDelete(row);
        },
        conditionFn: (row: EpinioServiceModel) => canDeleteService.value && row.canDelete,
      },
      {
        prop: 'editServiceModal',
        value: (row: EpinioServiceModel) => () => {
          serviceModal.value?.openEdit(row);
        },
        conditionFn: () => canEditService.value,
      }
    ];

  const processedRows = overrideTableRows(filtered, overrideProps);
  displayRows.value = processedRows;
})

onMounted(() => {
  store.dispatch('epinio/me');
  store.dispatch('epinio/findAll', { type: EPINIO_TYPES.APP });
  store.dispatch('epinio/findAll', { type: EPINIO_TYPES.SERVICE_INSTANCE });
  store.dispatch('epinio/findAll', { type: EPINIO_TYPES.NAMESPACE });
  store.dispatch('epinio/findAll', { type: EPINIO_TYPES.CATALOG_SERVICE });
  startPolling(['services'], store);
});

onUnmounted(() => {
  stopPolling(['services']);
})

const handleNavigate = (event: CustomEvent) => {
  router.push(event.detail.url);
};

const columns = [
  {
    field: 'stateDisplay',
    label: 'State',
    width: '100px',
    formatter: (_v: any, row: any) => makeStateTag(row)
  },
  {
    field: 'nameDisplay',
    label: 'Name',
    formatter: (_v: any, row: any) => {
      const el = document.createElement('a');

      el.textContent = row.nameDisplay || row.meta?.name || '';
      el.style.cursor = 'pointer';
      el.addEventListener('click', (e) => {
        e.preventDefault();
        e.stopPropagation();
        serviceModal.value?.openView(row);
      });

      return el;
    }
  },
  {
    field: 'catalog_service',
    label: 'Catalog Service',
    sortable:  false,
    formatter: (_v: any, row: any) => makeRouterLink(row.catalog_service, row.serviceLocation, router)
  },
  {
    field: 'catalog_service_version',
    label: 'Catalog Service Version'
  },
  {
    field: 'boundApps',
    label: 'Bound Applications',
    sortable:  false,
    formatter: (_v: any, row: any) => makeRouterLinksOrEmpty(row.applications, router)
  },
  {
    field: 'meta.createdAt',
    label: 'Age',
    formatter: 'age'
  }
];

function handleDeleted() {
  store.$router.push({
    name: 'epinio-c-cluster-resource',
    params: {
      ...store.$router.currentRoute.params,
      resource: 'catalogservices',
    },
  });
}

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("div", _hoisted_1, [
    _createVNode(_unref(Masthead), {
      schema: _ctx.value,
      resource: _ctx.value.id,
      "type-display": _unref(t)('epinio.catalogService.detail.servicesTitle', { catalogService: props.value.name })
    }, {
      subHeader: _withCtx(() => [
        _createElementVNode("p", _hoisted_2, _toDisplayString(_ctx.value.description ?? ''), 1)
      ]),
      createButton: _withCtx(() => [
        (availableActions.value.length > 0 && canEditCatalogService.value)
          ? (_openBlock(), _createElementBlock("trailhand-action-menu", {
              key: 0,
              actions: availableActions.value
            }, null, 8, _hoisted_3))
          : (_openBlock(), _createElementBlock("div", _hoisted_4))
      ]),
      _: 1,
      __: [0]
    }, 8, ["schema", "resource", "type-display"]),
    _cache[1] || (_cache[1] = _createTextVNode()),
    _createElementVNode("trailhand-table", {
      ref: (el) => { if (el) el.renderActions = _unref(makeActionMenu); },
      rows: displayRows.value,
      columns: columns,
      searchable: true,
      "key-field": "id",
      onNavigate: handleNavigate
    }, null, 40, _hoisted_5),
    _cache[2] || (_cache[2] = _createTextVNode()),
    _createVNode(ServiceDeleteModal, {
      ref_key: "serviceDeleteModal",
      ref: serviceDeleteModal
    }, null, 512),
    _cache[3] || (_cache[3] = _createTextVNode()),
    _createVNode(ServiceInstanceModal, {
      ref_key: "serviceModal",
      ref: serviceModal
    }, null, 512),
    _cache[4] || (_cache[4] = _createTextVNode()),
    _createVNode(CatalogServiceModal, {
      ref_key: "catalogServiceModal",
      ref: catalogServiceModal
    }, null, 512),
    _cache[5] || (_cache[5] = _createTextVNode()),
    _createVNode(CatalogServiceDeleteModal, {
      ref_key: "catalogServiceDeleteModal",
      ref: catalogServiceDeleteModal,
      onDeleted: handleDeleted
    }, null, 512)
  ]))
}
}

})